import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { tokensAPI } from '../../api/tokens';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import CreateTokenModal from './CreateTokenModal';
import { formatDate, formatTimeRemaining, getTokenStatusColor, formatPorts, copyToClipboard } from '../../utils/format';
import toast from 'react-hot-toast';

const TokensList = () => {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'expired', 'all'
  const [dropdownOpen, setDropdownOpen] = useState(null); // ID токена с открытым dropdown

  useEffect(() => {
    loadTokens();
  }, [activeTab]);

  // Закрытие dropdown при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen !== null && !event.target.closest('.dropdown-menu')) {
        setDropdownOpen(null);
      }
    };

    if (dropdownOpen !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const loadTokens = async () => {
    try {
      setLoading(true);
      // Загружаем все токены, включая истекшие
      const data = await tokensAPI.getList({ includeExpired: true });
      const allTokens = data.tokens || [];
      setTokens(allTokens);
    } catch (error) {
      toast.error(t('tokens.toasts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация токенов на основе активной вкладки
  const getFilteredTokens = () => {
    switch (activeTab) {
      case 'active':
        return tokens.filter(t => t.status === 'active');
      case 'expired':
        return tokens.filter(t => t.status === 'expired' || t.status === 'revoked');
      case 'all':
      default:
        return tokens;
    }
  };

  const filteredTokens = getFilteredTokens();
  
  // Подсчет статистики
  const stats = {
    active: tokens.filter(t => t.status === 'active').length,
    expired: tokens.filter(t => t.status === 'expired').length,
    revoked: tokens.filter(t => t.status === 'revoked').length,
    total: tokens.length
  };

  const handleDeleteToken = async (tokenIdentifier) => {
    if (!window.confirm(t('tokens.toasts.revokeConfirm'))) {
      return;
    }

    try {
      await tokensAPI.delete(tokenIdentifier);
      toast.success(t('tokens.toasts.revoked'));
      loadTokens();
    } catch (error) {
      toast.error(t('tokens.toasts.revokeError'));
    }
  };

  const handleCopyToken = async (tokenStr) => {
    const success = await copyToClipboard(tokenStr);
    if (success) {
      toast.success(t('tokens.toasts.copied'));
    } else {
      toast.error(t('tokens.toasts.copyError'));
    }
  };

  const handleCopyConnectionString = async (tokenStr, proxyId) => {
    // Генерируем строку подключения в формате host:token
    const connectionString = `${proxyId}:${tokenStr}`;
    const success = await copyToClipboard(connectionString);
    if (success) {
      toast.success('Строка подключения скопирована в буфер обмена');
    } else {
      toast.error('Ошибка копирования');
    }
  };

  // Генерируем команду для Windows
  const generateWindowsCommand = (tokenStr, tokenFromList) => {
    const forward = tokenFromList.ports && tokenFromList.ports.length > 0
      ? `${tokenFromList.ports[0].protocol}:${tokenFromList.ports[0].port}:127.0.0.1:3000`
      : 'tcp:80:127.0.0.1:3000';
    return `client.exe -host ${tokenFromList.proxyId} -token "${tokenStr}" -forwards "${forward}"`;
  };

  // Генерируем команду для Linux
  const generateLinuxCommand = (tokenStr, tokenFromList) => {
    const forward = tokenFromList.ports && tokenFromList.ports.length > 0
      ? `${tokenFromList.ports[0].protocol}:${tokenFromList.ports[0].port}:127.0.0.1:3000`
      : 'tcp:80:127.0.0.1:3000';
    return `./client -host ${tokenFromList.proxyId} -token "${tokenStr}" -forwards "${forward}"`;
  };

  // Копирование команды
  const handleCopyCommand = async (tokenStr, type, tokenFromList) => {
    let command;
    if (type === 'Windows') {
      command = generateWindowsCommand(tokenStr, tokenFromList);
    } else {
      command = generateLinuxCommand(tokenStr, tokenFromList);
    }

    const success = await copyToClipboard(command);
    if (success) {
      toast.success(`Команда для ${type} скопирована в буфер обмена`);
    } else {
      toast.error('Ошибка копирования');
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return t('tokens.status.active');
      case 'expired': return t('tokens.status.expired');
      case 'revoked': return t('tokens.status.revoked');
      default: return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('tokens.title')}</h1>
          <p className="mt-2 text-gray-600">{t('tokens.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-md hover:shadow-lg"
        >
          <div className="bg-blue-500 p-1.5 rounded-lg">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <span className="font-medium">{t('tokens.create')}</span>
        </button>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'active'
                  ? 'border-green-500 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('tokens.tabs.active')}</span>
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs font-semibold ${
                  activeTab === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {stats.active}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('expired')}
              className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'expired'
                  ? 'border-red-500 text-red-600 bg-red-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('tokens.tabs.expired')}</span>
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs font-semibold ${
                  activeTab === 'expired' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {stats.expired + stats.revoked}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'all'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span>{t('tokens.tabs.all')}</span>
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs font-semibold ${
                  activeTab === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {stats.total}
                </span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Tokens List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : filteredTokens.length === 0 ? (
        <Card className={activeTab === 'active' ? 'border-green-200 bg-green-50' : activeTab === 'expired' ? 'border-red-200 bg-red-50' : ''}>
          <div className="text-center py-12">
            <div className={`mx-auto h-20 w-20 rounded-full flex items-center justify-center ${
              activeTab === 'active' ? 'bg-green-100' : activeTab === 'expired' ? 'bg-red-100' : 'bg-gray-100'
            }`}>
              {activeTab === 'active' ? (
                <svg className="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : activeTab === 'expired' ? (
                <svg className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              )}
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">
              {activeTab === 'active' ? t('tokens.empty.noActive') :
               activeTab === 'expired' ? t('tokens.empty.noExpired') :
               t('tokens.empty.noTokens')}
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              {activeTab === 'active'
                ? t('tokens.empty.createForActive')
                : activeTab === 'expired'
                ? t('tokens.empty.expiredWillShow')
                : t('tokens.empty.createFirst')}
            </p>
            {activeTab !== 'expired' && (
              <Button className="mt-6" onClick={() => setShowCreateModal(true)}>
                <span className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>{t('tokens.empty.createButton')}</span>
                </span>
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTokens.map((token) => (
            <Card 
              key={token.id} 
              className={`hover:shadow-lg transition-all duration-200 ${
                token.status === 'active' 
                  ? 'border-l-4 border-l-green-500 hover:border-l-green-600' 
                  : token.status === 'expired'
                  ? 'border-l-4 border-l-red-400 opacity-75 hover:opacity-100'
                  : 'border-l-4 border-l-gray-400 opacity-60 hover:opacity-80'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`p-2 rounded-lg ${
                      token.status === 'active' 
                        ? 'bg-green-100' 
                        : token.status === 'expired'
                        ? 'bg-red-100'
                        : 'bg-gray-100'
                    }`}>
                      {token.status === 'active' ? (
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : token.status === 'expired' ? (
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {t('tokens.card.title')} #{token.id}
                      </h3>
                      <Badge variant={getTokenStatusColor(token.status)} className="mt-1">
                        {getStatusText(token.status)}
                      </Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                      </svg>
                      <span className="text-gray-600">{t('tokens.card.proxy')}:</span>
                      <span className="ml-2 font-medium text-gray-900">{token.proxyId}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-gray-600">{t('tokens.card.ports')}:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {formatPorts(token.ports)}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-gray-600">{t('tokens.card.created')}:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {formatDate(token.createdAt)}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-600">{t('tokens.card.expires')}:</span>
                      <span className="ml-2 font-medium">
                        {token.unlimited || (token.expiresAt && new Date(token.expiresAt).getFullYear() >= 9999) ? (
                          <span className="text-green-600 font-semibold">{t('tokens.card.unlimited')}</span>
                        ) : (
                          <span className={token.status === 'expired' ? 'text-red-600' : 'text-gray-900'}>
                            {formatDate(token.expiresAt)}
                          </span>
                        )}
                      </span>
                    </div>
                    
                    {token.status === 'active' && (
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-gray-600">{t('tokens.card.remaining')}:</span>
                        <span className="ml-2 font-medium text-blue-600">
                          {token.unlimited || (token.expiresAt && new Date(token.expiresAt).getFullYear() >= 9999) ? (
                            <span className="text-green-600 font-semibold">∞</span>
                          ) : (
                            formatTimeRemaining(token.expiresAt)
                          )}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span className="text-gray-600">{t('tokens.card.maxPorts')}:</span>
                      <span className="ml-2 font-medium text-gray-900">{token.maxPorts}</span>
                    </div>
                  </div>

                  {token.allowAnyNonPrivileged && (
                    <div className="mt-4">
                      <Badge variant="blue" className="text-xs">
                        <span className="flex items-center space-x-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{t('tokens.card.anyNonPrivileged')}</span>
                        </span>
                      </Badge>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2 ml-4">
                  {token.status === 'active' && token.token && (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleCopyToken(token.token)}
                        title={t('tokens.card.buttons.copyToken')}
                        className="hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                      </Button>

                      <div className="relative dropdown-menu">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setDropdownOpen(dropdownOpen === token.id ? null : token.id)}
                          title={t('tokens.card.buttons.copyCommand')}
                          className="hover:bg-green-50 hover:text-green-600 hover:border-green-300"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                          </svg>
                        </Button>

                        {dropdownOpen === token.id && (
                          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10 dropdown-menu">
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  handleCopyCommand(token.token, 'Windows', token);
                                  setDropdownOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
                                </svg>
                                <span>{t('tokens.card.buttons.windows')}</span>
                              </button>
                              <button
                                onClick={() => {
                                  handleCopyCommand(token.token, 'Linux', token);
                                  setDropdownOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12.004 0c-6.623 0-10 4.627-10 9 0 3.261 1.765 5.743 4.531 7.198-.126.946-.383 2.422-.795 3.38-.194.456.08.905.566.836 1.936-.276 3.908-1.426 4.738-2.055.314.045.639.07.971.07.621 0 1.221-.058 1.793-.168 1.578 1.095 3.768 2.394 5.895 2.697.486.069.76-.38.566-.836-.412-.958-.669-2.434-.795-3.38C22.239 14.743 24 12.261 24 9c0-4.373-3.377-9-10-9zm0 2c4.947 0 8 3.738 8 7 0 2.585-1.316 4.622-3.531 5.828.016.157.033.315.053.472.098.762.258 1.637.464 2.466-1.36-.247-2.749-.988-3.779-1.746-.419.082-.851.137-1.291.162-.39.022-.783.033-1.18.033-.323 0-.639-.025-.947-.073-1.03.758-2.419 1.499-3.779 1.746.206-.829.366-1.704.464-2.466.02-.157.037-.315.053-.472C3.316 13.622 2 11.585 2 9c0-3.262 3.053-7 8-7z"/>
                                </svg>
                                <span>{t('tokens.card.buttons.linux')}</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {token.status === 'active' && (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteToken(token.id)}
                      title={t('tokens.card.buttons.revoke')}
                      className="hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Token Modal */}
      <CreateTokenModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTokenCreated={loadTokens}
      />
    </div>
  );
};

export default TokensList;
